import { MatrixEvent, Room, RoomEvent } from 'matrix-js-sdk/src/matrix';
import { useEffect, useRef } from 'react';
import { Trans } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { ReactComponent as Emoji } from 'assets/icons/Emoji.svg';
import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import { getFormattedGuestDisplayName } from 'modules/MeetingChat/Messages/Message/utils';
import { isMessengerRoom } from 'modules/Messenger/components/ConversationsList/utils';
import useNotifications from 'modules/Notifications/NotificationsProvider/useNotifications';
import useWorkspacesContext from 'modules/Workspaces/WorkspacesProvider/useWorkspacesContext';
import { switchWorkspaceForMessengerRoom } from 'utils/roomHierarchyUtils';

interface ReactionRelation {
  rel_type?: string;
  event_id?: string;
  key?: string;
}

const useReactionNotifications = (): void => {
  const { matrixClient } = useMatrixContext();
  const { addNotification, removeNotification } = useNotifications();
  const navigate = useNavigate();
  const location = useLocation();
  const { setWorkspaceId, workspaceId, workspacesState } = useWorkspacesContext();

  const workspaceContextRef = useRef({ workspaceId, workspaces: workspacesState.workspaces });
  workspaceContextRef.current = { workspaceId, workspaces: workspacesState.workspaces };

  const pathnameRef = useRef(location.pathname);
  useEffect(() => {
    pathnameRef.current = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    const sessionStartTime = Date.now();
    const myUserId = matrixClient.getUserId();

    const handleTimeline = (event: MatrixEvent, room: Room | undefined, toStartOfTimeline?: boolean) => {
      if (toStartOfTimeline) return;
      if (!room) return;
      if (event.getType() !== 'm.reaction') return;
      // Skip events from before this session to avoid replaying historical timeline events
      // that the Matrix SDK fires during initial sync catch-up.
      if ((event.getTs() ?? 0) < sessionStartTime) return;

      const senderId = event.getSender();
      if (!senderId || senderId === myUserId) return;
      if (!isMessengerRoom(room)) return;

      const relation = (event.getContent() as { 'm.relates_to'?: ReactionRelation })['m.relates_to'];
      if (relation?.rel_type !== 'm.annotation') return;
      if (!relation.event_id) return;

      const targetEvent = room.findEventById(relation.event_id);
      if (!targetEvent) return;
      if (targetEvent.getSender() !== myUserId) return;

      if (pathnameRef.current.startsWith(`/messenger/${room.roomId}`)) return;

      const eventId = event.getId();
      if (!eventId) return;

      const notificationId = `messenger-reaction-${room.roomId}-${eventId}`;
      const senderName = getFormattedGuestDisplayName(room.getMember(senderId)?.rawDisplayName);
      const emoji = relation.key ?? '';
      const roomName = room.name;

      const title = (
        <Trans
          i18nKey="messenger.reacted_to_your_message"
          components={{ bold: <span className="font-medium" /> }}
          values={{ name: senderName, emoji, room: roomName }}
        />
      );

      addNotification({
        id: notificationId,
        icon: Emoji,
        variant: 'primary',
        title,
        content: null,
        isRead: false,
        timestamp: event.getTs() ?? Date.now(),
        onClick: () => {
          removeNotification(notificationId);
          navigate(`/messenger/${room.roomId}`, { state: { scrollToEventId: relation.event_id } });
          const { workspaceId: currentWorkspaceId, workspaces } = workspaceContextRef.current;
          switchWorkspaceForMessengerRoom(matrixClient, room.roomId, workspaces, currentWorkspaceId, setWorkspaceId);
        },
      });
    };

    const handleRedaction = (event: MatrixEvent, room: Room | undefined) => {
      if (!room) return;
      if (event.getType() !== 'm.reaction') return;
      const eventId = event.getId();
      if (!eventId) return;
      removeNotification(`messenger-reaction-${room.roomId}-${eventId}`);
    };

    matrixClient.on(RoomEvent.Timeline, handleTimeline);
    matrixClient.on(RoomEvent.Redaction, handleRedaction);
    return () => {
      matrixClient.off(RoomEvent.Timeline, handleTimeline);
      matrixClient.off(RoomEvent.Redaction, handleRedaction);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matrixClient]);
};

export default useReactionNotifications;
